import { ArrowRight, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../lib/toast";
import { Batch, Product, productService } from "../../services/product.service";
import {
  CreateStockTransferDto,
  CreateStockTransferItemDto,
  stockTransferService,
} from "../../services/stock-transfer.service";
import { Store, storeService } from "../../services/store.service";

interface ProductSearchResult {
  product: Product;
  batches: Batch[];
}

interface TransferItemWithDetails extends CreateStockTransferItemDto {
  productName: string;
  sku: string;
  batchNumber: string;
  batchExpiry: string;
  availableQty: number;
}

export function CreateStockTransferPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [sourceStoreId, setSourceStoreId] = useState("");
  const [destStoreId, setDestStoreId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<TransferItemWithDetails[]>([]);

  // Product search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const bundledItems = items.reduce<
    Array<{
      productId: string;
      productName: string;
      sku: string;
      batchCount: number;
      totalQty: number;
    }>
  >((summary, item) => {
    const existing = summary.find(
      (entry) => entry.productId === item.productId,
    );
    if (existing) {
      existing.batchCount += 1;
      existing.totalQty += item.qtyRequested;
      return summary;
    }

    summary.push({
      productId: item.productId,
      productName: item.productName,
      sku: item.sku,
      batchCount: 1,
      totalQty: item.qtyRequested,
    });
    return summary;
  }, []);

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchProducts();
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchTerm]);

  const loadStores = async () => {
    try {
      const data = await storeService.getAll();
      setStores(data || []);
    } catch (err) {
      showToast.error("Failed to load stores");
    }
  };

  const searchProducts = async () => {
    try {
      // Fetch products matching search term
      const productsData = await productService.getProducts({
        search: searchTerm,
        limit: 10,
      });

      if (!productsData.data.length) {
        setSearchResults([]);
        return;
      }

      // For each product, fetch its batches
      // Ideally this should be a single API call like /products/search-with-batches
      // But we'll do it this way for now based on available services
      const results: ProductSearchResult[] = [];
      const allBatches = await productService.getBatches(); // TODO: Optimize this

      for (const product of productsData.data) {
        const productBatches = allBatches.filter(
          (b: Batch) => b.productId === product.id && b.quantity > 0,
        );
        if (productBatches.length > 0) {
          results.push({
            product,
            batches: productBatches,
          });
        }
      }

      setSearchResults(results);
      setShowSearchResults(true);
    } catch (err: any) {
      console.error("Search failed:", err);
    }
  };

  const addItem = (product: Product, batch: Batch) => {
    // Check if item already exists
    const exists = items.find(
      (item) => item.productId === product.id && item.batchId === batch.id,
    );
    if (exists) {
      showToast.error("This product batch is already added");
      return;
    }

    const newItem: TransferItemWithDetails = {
      productId: product.id,
      batchId: batch.id,
      qtyRequested: 1,
      productName: product.name,
      sku: product.sku,
      batchNumber: batch.batchNumber,
      batchExpiry: batch.expiryDate,
      availableQty: batch.quantity,
    };

    setItems([...items, newItem]);
    setSearchTerm("");
    setShowSearchResults(false);
  };

  const updateItemQty = (index: number, qty: number) => {
    const updatedItems = [...items];
    const item = updatedItems[index];

    if (qty > item.availableQty) {
      showToast.error(
        `Only ${item.availableQty} units available in this batch`,
      );
      return;
    }

    updatedItems[index] = { ...item, qtyRequested: qty };
    setItems(updatedItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sourceStoreId) {
      showToast.error("Please select source store");
      return;
    }
    if (!destStoreId) {
      showToast.error("Please select destination store");
      return;
    }
    if (sourceStoreId === destStoreId) {
      showToast.error("Source and destination stores cannot be same");
      return;
    }
    if (items.length === 0) {
      showToast.error("Please add items to transfer");
      return;
    }

    const payload: CreateStockTransferDto = {
      sourceStoreId,
      destStoreId,
      notes,
      items: items.map(({ productId, batchId, qtyRequested }) => ({
        productId,
        batchId,
        qtyRequested,
      })),
    };

    try {
      setLoading(true);
      await stockTransferService.create(payload);
      showToast.success("Stock transfer created successfully");
      navigate("/stock-transfers");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to create transfer",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/stock-transfers")}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to List
        </button>
        <h1 className="text-2xl font-bold text-gray-900">New Stock Transfer</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Store Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Transfer Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source Store (From) <span className="text-red-500">*</span>
              </label>
              <select
                value={sourceStoreId}
                onChange={(e) => setSourceStoreId(e.target.value)}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Source Store</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="hidden md:flex justify-center pt-6">
              <ArrowRight className="text-gray-400 w-6 h-6" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination Store (To) <span className="text-red-500">*</span>
              </label>
              <select
                value={destStoreId}
                onChange={(e) => setDestStoreId(e.target.value)}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Destination Store</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional notes for this transfer..."
            />
          </div>
        </div>

        {/* Items Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Items</h2>
          <p className="mb-4 text-sm text-gray-500">
            The same product can be transferred from multiple batches in one
            bundled transfer request.
          </p>

          {/* Search */}
          <div className="mb-6 relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add Products
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search products by name or SKU..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Search Results */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {searchResults.map(({ product, batches }) => (
                  <div key={product.id} className="border-b last:border-b-0">
                    <div className="px-4 py-2 bg-gray-50 text-sm font-medium text-gray-700">
                      {product.name} ({product.sku})
                    </div>
                    {batches.map((batch) => (
                      <button
                        key={batch.id}
                        type="button"
                        onClick={() => addItem(product, batch)}
                        className="w-full px-4 py-2 text-left hover:bg-blue-50 flex justify-between items-center text-sm"
                      >
                        <div>
                          <span className="text-gray-600">Batch: </span>
                          <span className="font-medium">
                            {batch.batchNumber}
                          </span>
                          <span className="text-gray-400 mx-2">|</span>
                          <span className="text-gray-600">Exp: </span>
                          <span>
                            {new Date(batch.expiryDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="text-gray-600">
                          Qty:{" "}
                          <span className="font-medium">{batch.quantity}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {showSearchResults &&
              searchTerm.length >= 2 &&
              searchResults.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-center text-gray-500">
                  No products found with available stock
                </div>
              )}
          </div>

          {/* Selected Items Table */}
          {items.length > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {bundledItems.map((item) => (
                  <div
                    key={item.productId}
                    className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3"
                  >
                    <div className="text-sm font-semibold text-blue-900">
                      {item.productName}
                    </div>
                    <div className="text-xs text-blue-700">{item.sku}</div>
                    <div className="mt-2 text-sm text-blue-800">
                      {item.batchCount} batch{item.batchCount > 1 ? "es" : ""},
                      total transfer qty {item.totalQty}
                    </div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Batch Info
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Available
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Transfer Qty
                      </th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {items.map((item, index) => (
                      <tr key={`${item.productId}-${item.batchId}`}>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {item.productName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.sku}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div>Batch: {item.batchNumber}</div>
                          <div className="text-gray-500 text-xs">
                            Exp:{" "}
                            {new Date(item.batchExpiry).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {item.availableQty}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            max={item.availableQty}
                            value={item.qtyRequested}
                            onChange={(e) =>
                              updateItemQty(
                                index,
                                parseInt(e.target.value) || 0,
                              )
                            }
                            className="w-24 px-2 py-1 border rounded text-center mx-auto block"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
              No items added. Search and select product batches to transfer.
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate("/stock-transfers")}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || items.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Creating..." : "Create Transfer Request"}
          </button>
        </div>
      </form>
    </div>
  );
}
